import React from 'react';

interface TestSession {
  id: string;
  date: string;
  eps: number;
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
  trapBarDL: number;
  backSquat: number;
  frontSquat: number;
  splitSquat: number;
  cmjHeight: number;
  sqjHeight: number;
  sprint10yd: number;
  sprint20yd: number;
  medBallThrow: number;
  rsi: number;
  dropJumpHeight: number;
  broadJump: number;
  tripleJump: number;
  pogoHeight: number;
}

interface AthleteProfile {
  id: string;
  name: string;
  age?: number;
  sport?: string;
  position?: string;
  heightIn?: number;
  weightLbs?: number;
  trainingAge?: number;
  injuryHistory?: string;
  seasonPhase?: string;
  avatarInitials?: string;
}

interface RawDataTableProps {
  session: TestSession;
  athlete: AthleteProfile;
}

const ENGINE_STANDARDS = [
  { label: 'Trap Bar Deadlift 1RM', key: 'trapBarDL', unit: 'lbs', elite: '2.5×BW', high: '2.1×BW', developing: '1.6×BW' },
  { label: 'Back Squat 1RM', key: 'backSquat', unit: 'lbs', elite: '2.0×BW', high: '1.7×BW', developing: '1.3×BW' },
  { label: 'Front Squat 1RM', key: 'frontSquat', unit: 'lbs', elite: '1.7×BW', high: '1.4×BW', developing: '1.1×BW' },
  { label: 'Split Squat (best leg)', key: 'splitSquat', unit: 'lbs', elite: '1.5×BW', high: '1.2×BW', developing: '0.9×BW' },
];

const IGNITION_STANDARDS = [
  { label: 'Countermovement Jump', key: 'cmjHeight', unit: 'in', elite: '32 in', high: '28 in', developing: '22 in' },
  { label: 'Squat Jump', key: 'sqjHeight', unit: 'in', elite: '28 in', high: '24 in', developing: '18 in' },
  { label: '10-Yard Sprint', key: 'sprint10yd', unit: 's', elite: '1.45 s', high: '1.55 s', developing: '1.70 s' },
  { label: '20-Yard Sprint', key: 'sprint20yd', unit: 's', elite: '2.55 s', high: '2.70 s', developing: '2.95 s' },
  { label: 'Med Ball Throw', key: 'medBallThrow', unit: 'ft', elite: '45 ft', high: '38 ft', developing: '28 ft' },
];

const BOUNCE_STANDARDS = [
  { label: 'RSI Score', key: 'rsi', unit: '', elite: '2.5', high: '1.8', developing: '1.0' },
  { label: 'Drop Jump Height', key: 'dropJumpHeight', unit: 'in', elite: '26 in', high: '22 in', developing: '16 in' },
  { label: 'Broad Jump', key: 'broadJump', unit: 'in', elite: '110 in', high: '98 in', developing: '80 in' },
  { label: 'Triple Jump', key: 'tripleJump', unit: 'in', elite: '330 in', high: '298 in', developing: '250 in' },
  { label: 'Pogo Jump Height', key: 'pogoHeight', unit: 'in', elite: '12 in', high: '10 in', developing: '7 in' },
];

function DataRow({ label, value, unit, elite, high, developing }: {
  label: string; value: number; unit: string; elite: string; high: string; developing: string;
}) {
  return (
    <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
      <td className="py-2.5 px-3 text-sm text-zinc-300">{label}</td>
      <td className="py-2.5 px-3 text-sm font-mono tabular-nums font-semibold text-zinc-100">
        {value}{unit ? ` ${unit}` : ''}
      </td>
      <td className="py-2.5 px-3 text-xs font-mono" style={{ color: '#FF3B30' }}>{elite}</td>
      <td className="py-2.5 px-3 text-xs font-mono text-emerald-400">{high}</td>
      <td className="py-2.5 px-3 text-xs font-mono text-amber-400">{developing}</td>
    </tr>
  );
}

function SectionTable({ title, standards, session }: { title: string; standards: typeof ENGINE_STANDARDS; session: TestSession }) {
  return (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-zinc-300 mb-2 px-1">{title}</h4>
      <div className="overflow-x-auto scrollbar-thin rounded-xl border border-zinc-800">
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-800/40">
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Test</th>
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Result</th>
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider" style={{ color: '#FF3B30' }}>Elite</th>
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-emerald-500">High</th>
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-amber-500">Developing</th>
            </tr>
          </thead>
          <tbody>
            {standards.map(s => (
              <DataRow
                key={`raw-${s.key}`}
                label={s.label}
                value={(session as any)[s.key]}
                unit={s.unit}
                elite={s.elite}
                high={s.high}
                developing={s.developing}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RawDataTable({ session, athlete }: RawDataTableProps) {
  return (
    <div className="card p-5">
      <h3 className="section-header mb-1">Raw Test Data vs Standards</h3>
      <p className="text-xs text-zinc-500 mb-5">
        {athlete.name} · BW: {athlete.weightLbs ?? 0} lbs · Standards based on absolute performance benchmarks
      </p>
      <SectionTable title="Engine — Strength Tests" standards={ENGINE_STANDARDS} session={session} />
      <SectionTable title="Ignition — Power & Speed Tests" standards={IGNITION_STANDARDS} session={session} />
      <SectionTable title="Bounce — Reactive & Elastic Tests" standards={BOUNCE_STANDARDS} session={session} />
    </div>
  );
}